import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { getAddressLabel } from '../../util/dataExtractors';
import { IconEdit, IconDelete, IconSpinner } from '../../components';

import css from './SavedAddressCard.module.css';

const SavedAddressCard = props => {
  const {
    address,
    index,
    onEdit,
    onDelete,
    onSetDefault,
    setDefaultInProgress,
    className,
  } = props;

  const intl = useIntl();
  const label = getAddressLabel(address, index, intl);
  const isDefault = address.isDefault;

  const handleEdit = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) onEdit(address);
  };

  const handleDelete = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) onDelete(address.id);
  };

  const handleSetDefault = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onSetDefault && !isDefault) onSetDefault(address.id);
  };

  const classes = classNames(css.root, className, {
    [css.isDefault]: isDefault,
  });

  return (
    <div className={classes}>
      <div className={css.header}>
        <span className={css.label}>{label}</span>
        {isDefault && (
          <span className={css.defaultBadge}>
            {intl.formatMessage({ id: 'SavedAddresses.defaultBadge' })}
          </span>
        )}
      </div>

      <div className={css.addressContent}>
        <p className={css.addressLine}>{address.line1}</p>
        {address.line2 && <p className={css.addressLine}>{address.line2}</p>}
        <p className={css.addressLine}>
          {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
        </p>
        <p className={css.addressLine}>{address.country}</p>
        {address.phone_number && (
          <p className={css.phoneNumber}>{address.phone_number}</p>
        )}
      </div>

      <div className={css.actions}>
        <button
          type="button"
          className={css.actionButton}
          onClick={handleEdit}
          title={intl.formatMessage({ id: 'SavedAddresses.edit' })}
        >
          <IconEdit className={css.actionIcon} />
          <span>{intl.formatMessage({ id: 'SavedAddresses.edit' })}</span>
        </button>

        <button
          type="button"
          className={classNames(css.actionButton, css.deleteButton)}
          onClick={handleDelete}
          title={intl.formatMessage({ id: 'SavedAddresses.delete' })}
        >
          <IconDelete className={css.actionIcon} />
          <span>{intl.formatMessage({ id: 'SavedAddresses.delete' })}</span>
        </button>

        {!isDefault && (
          <button
            type="button"
            className={css.actionButton}
            onClick={handleSetDefault}
            disabled={setDefaultInProgress}
            title={intl.formatMessage({ id: 'SavedAddresses.setAsDefault' })}
          >
            {setDefaultInProgress ? (
              <IconSpinner className={css.spinner} />
            ) : (
              <span>{intl.formatMessage({ id: 'SavedAddresses.setAsDefault' })}</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

SavedAddressCard.defaultProps = {
  className: null,
  onEdit: null,
  onDelete: null,
  onSetDefault: null,
  setDefaultInProgress: false,
};

SavedAddressCard.propTypes = {
  address: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    line1: PropTypes.string.isRequired,
    line2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string,
    postal_code: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    phone_number: PropTypes.string,
    isDefault: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSetDefault: PropTypes.func,
  setDefaultInProgress: PropTypes.bool,
  className: PropTypes.string,
};

export default SavedAddressCard;
